"use client";
"use no memo";

// TanStack Table v8 exposes mutable instances that React Compiler cannot safely memoize.
// Remove this boundary after upgrading to a compiler-compatible TanStack Table release.

import { rankItem } from "@tanstack/match-sorter-utils";
import {
  ColumnDef,
  ColumnFiltersState,
  type Table as TanstackTable,
  type Row,
  type ColumnSizingState,
  SortingState,
  type Updater,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import { useRouter } from "next/navigation";
import { type UseQueryStatesKeysMap, type Values } from "nuqs";
import * as React from "react";

import { ContextMenu, ContextMenuTrigger } from "@/src/components/shadcn/ui/context-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/src/components/shadcn/ui/table";
import { DataTablePagination, usePaginationState } from "@/src/components/tables/data-table-pagination";
import { getFillColumnSizing, useColumnSizing } from "@/src/components/tables/use-column-sizing";
import { useDataTableState } from "@/src/components/tables/use-data-table-state";
import { useDataTableSearchContext } from "@/src/context/data-table-search-context";
import { shouldHandleGlobalEvent } from "@/src/lib/browser-events";

export type ColumnFilters = Values<UseQueryStatesKeysMap>;

function transformColumnFilters(columnFilters?: ColumnFilters): ColumnFiltersState | undefined {
  return columnFilters
    ? Object.entries(columnFilters).reduce<ColumnFiltersState>((acc, [key, value]) => {
        if ((Array.isArray(value) && value.length > 0) || value) {
          acc.push({ id: key, value });
        }

        return acc;
      }, [])
    : undefined;
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  columnFilters?: ColumnFilters;
  defaultColumnVisibility?: VisibilityState;
  defaultSorting?: SortingState;
  ToolbarComponent?: React.ComponentType<{
    table: TanstackTable<TData>;
  }>;
  getRowUrl: (row: TData) => string;
  getRowId?: (originalRow: TData, index: number, parent?: Row<TData>) => string;
  isRowDisabled?: (row: TData) => boolean;
  blurRow?: (row: TData) => boolean;
  getRowClassName?: (row: TData) => string | undefined;
  emptyMessage?: string;
  isLoading?: boolean;
  showLoadingMessage?: boolean;
  showPagination?: boolean;
  /** When false, row navigation does not save the list query and does not bind window j/k. */
  captureListUrl?: boolean;
  RowContextMenuComponent?: React.ComponentType<{ row: Row<TData> }>;
  columnSizingStorageKey?: string;
  // Server-side pagination props
  serverSidePagination?: boolean;
  totalCount?: number;
}

function DataTableRowWithContextMenu<TData>({
  row,
  RowContextMenuComponent,
  children,
}: {
  row: Row<TData>;
  RowContextMenuComponent?: React.ComponentType<{ row: Row<TData> }>;
  children: React.ReactElement;
}) {
  const [isOpen, setIsOpen] = React.useState(false);

  if (!RowContextMenuComponent) {
    return children;
  }

  return (
    <ContextMenu onOpenChange={setIsOpen}>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      {isOpen && <RowContextMenuComponent row={row} />}
    </ContextMenu>
  );
}

export function DataTable<TData, TValue>({
  columns,
  data,
  columnFilters,
  defaultColumnVisibility,
  defaultSorting,
  ToolbarComponent,
  getRowUrl,
  getRowId,
  isRowDisabled = () => false,
  blurRow = () => false,
  getRowClassName,
  emptyMessage = "Current list is empty.",
  isLoading = false,
  showLoadingMessage = true,
  showPagination = true,
  captureListUrl = true,
  RowContextMenuComponent,
  columnSizingStorageKey,
  serverSidePagination = false,
  totalCount,
}: DataTableProps<TData, TValue>) {
  const tableRef = React.useRef<React.ElementRef<typeof Table>>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);
  const [rowSelection, setRowSelection] = React.useState({});
  const [isInteracting, setIsInteracting] = React.useState(false);
  const [pagination, setPagination] = usePaginationState();
  const [dataTableState, setDataTableState] = useDataTableState({ defaultColumnVisibility, defaultSorting });
  const router = useRouter();
  const { storeSearchContext } = useDataTableSearchContext();
  const [highlightedRowIndex, setHighlightedRowIndex] = React.useState<number>(-1);
  const { query, columnVisibility, sorting } = dataTableState;
  const [columnSizing, setColumnSizing] = useColumnSizing(columnSizingStorageKey);
  const enableColumnResizing = columnSizingStorageKey !== undefined;
  const effectiveColumnSizing =
    enableColumnResizing && Object.keys(columnSizing).length === 0
      ? getFillColumnSizing(columns, columnVisibility, containerWidth)
      : columnSizing;

  const table = useReactTable({
    data,
    columns,
    getRowId,
    state: {
      sorting,
      pagination,
      columnVisibility,
      columnSizing: effectiveColumnSizing,
      rowSelection,
      globalFilter: query,
      columnFilters: transformColumnFilters(columnFilters) ?? [],
    },
    autoResetPageIndex: false,
    enableColumnResizing,
    columnResizeMode: "onChange",
    defaultColumn: { minSize: 80 },
    onColumnSizingChange: (updater: Updater<ColumnSizingState>) => {
      setColumnSizing(typeof updater === "function" ? updater(effectiveColumnSizing) : updater);
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: (updater: Updater<SortingState>) => {
      const newSorting = typeof updater === "function" ? updater([]) : updater;

      setDataTableState({
        sorting: newSorting,
      });
    },
    onColumnVisibilityChange: (updater: Updater<Record<string, boolean>>) => {
      const newHiddenColumns = typeof updater === "function" ? updater({}) : updater;

      setDataTableState({
        columnVisibility: {
          ...dataTableState.columnVisibility,
          ...newHiddenColumns,
        },
      });
    },
    getCoreRowModel: getCoreRowModel(),
    // Disable client-side filtering for server-side pagination
    getFilteredRowModel: serverSidePagination ? undefined : getFilteredRowModel(),
    globalFilterFn: serverSidePagination
      ? undefined
      : (row, columnId, value, addMeta) => {
          // See https://tanstack.com/table/v8/docs/api/features/global-filtering#filter-meta
          const itemRank = rankItem(row.getValue(columnId), value);

          addMeta(itemRank);

          return itemRank.passed;
        },
    // Only use client-side pagination if not using server-side pagination
    getPaginationRowModel: serverSidePagination ? undefined : getPaginationRowModel(),
    getSortedRowModel: serverSidePagination ? undefined : getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    // Server-side pagination configuration
    manualPagination: serverSidePagination,
    manualFiltering: serverSidePagination,
    manualSorting: serverSidePagination,
    pageCount: serverSidePagination && totalCount ? Math.ceil(totalCount / pagination.pageSize) : undefined,
  });

  React.useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    if (!enableColumnResizing || !scrollContainer) return;

    const observer = new ResizeObserver(() => setContainerWidth(scrollContainer.clientWidth));

    observer.observe(scrollContainer);

    return () => observer.disconnect();
  }, [enableColumnResizing]);

  const fillColumnId = table.getVisibleLeafColumns().findLast((column) => column.getCanResize())?.id;

  // Keep a reference to the reactTable so we can runs side effects
  const reactTableRef = React.useRef(table);

  React.useEffect(() => {
    reactTableRef.current = table;
  }, [table]);

  React.useEffect(() => {
    if (reactTableRef.current.getState().pagination.pageIndex > 0) {
      setPagination({
        pageIndex: 0,
      });
    }
  }, [columnFilters, query, setPagination]);

  React.useEffect(() => {
    if (!captureListUrl) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!shouldHandleGlobalEvent(e)) {
        return;
      }

      if (e.altKey || e.ctrlKey || e.metaKey) return;

      const isMoveDownEvent = e.key === "ArrowDown" || e.key.toLowerCase() === "j";
      const isMoveUpEvent = e.key === "ArrowUp" || e.key.toLowerCase() === "k";

      if (e.key === " " && isInteracting) {
        e.preventDefault();

        const highlightedRow = table.getRowModel().rows[highlightedRowIndex];

        if (!highlightedRow) return;

        table.setRowSelection((state) => {
          return {
            ...state,
            [highlightedRow.id]: !state[highlightedRow.id],
          };
        });
      } else if (isMoveDownEvent || isMoveUpEvent) {
        e.preventDefault();

        setIsInteracting(true);

        setHighlightedRowIndex((prev) => {
          const rows = table.getRowModel().rows;

          if (prev === -1) return 0;

          const nextIndex = prev + (isMoveDownEvent ? 1 : -1);

          return Math.max(0, Math.min(nextIndex, rows.length - 1));
        });
      } else if (e.key === "Enter" && highlightedRowIndex !== -1) {
        const row = table.getRowModel().rows[highlightedRowIndex];

        if (row && !isRowDisabled(row.original)) {
          const url = getRowUrl(row.original);

          storeSearchContext();

          router.push(url);
        }
      }
    };

    function handleClick(e: MouseEvent): void {
      const target = e.target as HTMLElement;

      if (!target.contains(tableRef.current)) {
        setIsInteracting(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    window.addEventListener("click", handleClick);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);

      window.removeEventListener("click", handleClick);
    };
  }, [table, router, getRowUrl, isRowDisabled, highlightedRowIndex, isInteracting, storeSearchContext, captureListUrl]);

  const tableRows = table.getRowModel().rows.map((row, index) => {
    const isDisabled = isRowDisabled(row.original);
    const isBlurred = blurRow ? blurRow(row.original) : false;

    const rowClassName = clsx(
      isDisabled ? "opacity-50 hover:bg-transparent" : "",
      highlightedRowIndex === index ? "bg-muted" : "",
      isBlurred ? "opacity-70" : "",
      getRowClassName?.(row.original)
    );

    const onClick = (e: React.MouseEvent) => {
      e.stopPropagation();

      if (isDisabled) {
        return;
      }

      const url = getRowUrl(row.original);

      if (captureListUrl) storeSearchContext();

      router.push(url);
    };

    return (
      <DataTableRowWithContextMenu key={row.id} row={row} RowContextMenuComponent={RowContextMenuComponent}>
        <TableRow className={rowClassName} data-state={row.getIsSelected() && "selected"} onClick={onClick}>
          {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id} className={clsx(enableColumnResizing && "overflow-hidden")}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          ))}
        </TableRow>
      </DataTableRowWithContextMenu>
    );
  });

  return (
    <div className="flex flex-col h-full">
      {ToolbarComponent ? (
        <div className="border-b bg-background">
          <ToolbarComponent table={table} />
        </div>
      ) : null}
      <div ref={scrollContainerRef} className="flex-1 h-full relative overflow-auto isolate">
        <Table
          ref={tableRef}
          includeWrapper={false}
          className={clsx(enableColumnResizing && "table-fixed [&_td_.truncate]:!max-w-full")}
          style={enableColumnResizing ? { minWidth: table.getTotalSize() } : undefined}
        >
          <TableHeader className="bg-muted sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="border-none">
                {headerGroup.headers.map((header) => {
                  const isFillColumn = header.column.id === fillColumnId;

                  return (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      className={clsx(enableColumnResizing && "relative overflow-hidden")}
                      style={enableColumnResizing && !isFillColumn ? { width: header.getSize() } : undefined}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      {enableColumnResizing && !isFillColumn && header.column.getCanResize() && (
                        <div
                          role="separator"
                          aria-orientation="vertical"
                          aria-label={`Resize ${header.column.id} column`}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          onDoubleClick={() => header.column.resetSize()}
                          className={clsx(
                            "absolute right-0 top-0 h-full w-1.5 cursor-col-resize touch-none select-none hover:bg-foreground/20",
                            header.column.getIsResizing() && "bg-foreground/40"
                          )}
                        />
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              tableRows
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getVisibleLeafColumns().length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {isLoading && showLoadingMessage && <p>Loading...</p>}
                  {!isLoading && emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {showPagination && (
        <div className="border-t mt-auto bg-background">
          <DataTablePagination table={table} totalCount={totalCount} />
        </div>
      )}
    </div>
  );
}
